'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { type Address, ChainUtils } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { InterfaceChainUtils, parseApiAsset, toBigInt, ZERO } from '@morpho-org/dapp-core';
import { NetworkAvatar } from '@morpho-org/ui-v6/client';
import { formatters, getFirstAndRest } from '@morpho-org/ui-v6/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { abbreviateAddress } from '@repo/utils';
import { useId } from 'react';
import { ButtonLink } from '@/components/common/ButtonLink';
import { CollateralExposureStack } from '@/components/common/CollateralExposureStack';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import { InfoHoverCardTrigger } from '@/components/common/InfoHoverCardTrigger';
import RewardsHoverCard from '@/components/common/RewardsHoverCard';
import { TokenWithHoverCard } from '@/components/common/TokenWithHoverCard';
import { WarningIcon } from '@/components/common/WarningIcon';
import { vaultIncludesDonations } from '@/config/donation-vaults';
import { aggregateVaultV2Warnings } from '@/helpers/aggregateVaultWarnings';
import { getParsedValue } from '@/helpers/formatters';
import { splitValueAndUnit } from '@/helpers/number';
import { getCuratorUrl } from '@/helpers/routing';
import { mapVaultApyCardRows } from '@/helpers/vaultApyCardRows';
import { isVaultV2RateCapped, VAULT_V2_INTEREST_PAUSED_LABEL, VAULT_V2_INTEREST_PAUSED_TOOLTIP } from '@/helpers/vaultV2RateCap';
import { getWarningsColor } from '@/helpers/warnings';
import { useVaultV2Apy } from '@/hooks/useVaultV2Apy';
import { trackMetricTooltipOpenChange } from '@/lib/analytics/client/trackers/metric-tooltip';
import type { VaultV2HeroData } from './VaultV2Hero.mapper';
import { LinkifiedVaultDescription } from '../../components/VaultHero/LinkifiedVaultDescription';
import { ResponsiveVaultTitle } from '../../components/VaultHero/ResponsiveVaultTitle';
import { VaultV2LiquidityHoverCard } from './VaultV2LiquidityHoverCard';
export interface VaultV2HeroClientProps {
  address: Address;
  vaultV2: VaultV2HeroData;
  pendingActionsSlot?: ReactNode;
}
const VaultV2HeroClient = t0 => {
  const $ = _c(161);
  const {
    address,
    vaultV2,
    pendingActionsSlot
  } = t0;
  const chainId = vaultV2.chainId;
  const aggregatedWarnings = aggregateVaultV2Warnings(vaultV2.warnings);
  const asset = getParsedValue(vaultV2.asset, parseApiAsset);
  let t1;
  if ($[0] !== address || $[1] !== vaultV2.apyBreakdown || $[2] !== vaultV2.asset || $[3] !== vaultV2.managementFee || $[4] !== vaultV2.maxRate || $[5] !== vaultV2.netApy || $[6] !== vaultV2.netApyExcludingRewards || $[7] !== vaultV2.performanceFee || $[8] !== vaultV2.rewards) {
    t1 = {
      vaultV2: {
        address,
        asset: vaultV2.asset,
        performanceFee: vaultV2.performanceFee,
        managementFee: vaultV2.managementFee,
        netApyExcludingRewards: vaultV2.netApyExcludingRewards,
        netApy: vaultV2.netApy,
        apyBreakdown: vaultV2.apyBreakdown,
        maxRate: vaultV2.maxRate,
        rewards: vaultV2.rewards
      }
    };
    $[0] = address;
    $[1] = vaultV2.apyBreakdown;
    $[2] = vaultV2.asset;
    $[3] = vaultV2.managementFee;
    $[4] = vaultV2.maxRate;
    $[5] = vaultV2.netApy;
    $[6] = vaultV2.netApyExcludingRewards;
    $[7] = vaultV2.performanceFee;
    $[8] = vaultV2.rewards;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const {
    apy,
    netApy,
    performanceFee,
    managementFee,
    maxRate,
    points,
    rewards
  } = useVaultV2Apy(t1);
  const totalDeposits = formatters.short.digits(2).trailingZero(false).default("-").unit(asset?.symbol).of(toBigInt(vaultV2.totalAssets), vaultV2.assetDecimals);
  let t2;
  if ($[10] !== vaultV2.totalAssetsUsd) {
    t2 = splitValueAndUnit(formatters.short.digits(2).trailingZero(false).default("-").of(getParsedValue(vaultV2.totalAssetsUsd, safeParseNumber), 18));
    $[10] = vaultV2.totalAssetsUsd;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const totalDepositsUsd = t2;
  const t3 = vaultV2.name ?? "";
  let t4;
  if ($[12] !== t3) {
    t4 = getFirstAndRest(t3);
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const formattedTitle = t4;
  const titleColor = getWarningsColor(aggregatedWarnings);
  let t5;
  if ($[14] !== chainId) {
    t5 = InterfaceChainUtils.getMetadata(chainId);
    $[14] = chainId;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const chain = t5;
  let t6;
  if ($[16] !== netApy) {
    t6 = formatters.percent.digits(2).default("-").max(10).of(netApy, 18);
    $[16] = netApy;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const formattedNetApy = t6;
  const netApyLabelId = useId();
  let includesDonations;
  let isRateCapped;
  let t7;
  if ($[18] !== address || $[19] !== apy || $[20] !== chainId || $[21] !== rewards || $[22] !== vaultV2.apyBreakdown || $[23] !== vaultV2.grossApy || $[24] !== vaultV2.maxApy) {
    isRateCapped = isVaultV2RateCapped(vaultV2.grossApy, vaultV2.maxApy);
    includesDonations = vaultIncludesDonations(chainId, address);
    t7 = mapVaultApyCardRows({
      breakdown: vaultV2.apyBreakdown ?? null,
      netApyExcludingRewards: apy == null ? null : Number(apy) / 1000000000000000000,
      rewards: (rewards ?? []).map(_temp),
      includesDonations,
      isRateCapped,
      maxApy: vaultV2.maxApy ?? null
    });
    $[18] = address;
    $[19] = apy;
    $[20] = chainId;
    $[21] = rewards;
    $[22] = vaultV2.apyBreakdown;
    $[23] = vaultV2.grossApy;
    $[24] = vaultV2.maxApy;
    $[25] = includesDonations;
    $[26] = isRateCapped;
    $[27] = t7;
  } else {
    includesDonations = $[25];
    isRateCapped = $[26];
    t7 = $[27];
  }
  const {
    hasCard
  } = t7;
  const hasNetApyCard = hasCard || !!points?.length;
  let t8;
  if ($[28] !== vaultV2.collateralExposures) {
    t8 = vaultV2.collateralExposures?.map(_temp2);
    $[28] = vaultV2.collateralExposures;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const collateralExposures = t8;
  let t9;
  if ($[30] !== vaultV2.totalAssetsUsd) {
    t9 = getParsedValue(vaultV2.totalAssetsUsd, safeParseNumber);
    $[30] = vaultV2.totalAssetsUsd;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const totalAssetsUsd = t9;
  let t10;
  if ($[32] !== vaultV2.maxApy) {
    t10 = vaultV2.maxApy === 0 && <div className="bg-warning p-md gap-xs flex w-full items-center rounded-[20px]" data-testid="vault-v2-interest-paused-banner"><span className="text-warning-foreground text-xs">{VAULT_V2_INTEREST_PAUSED_LABEL}. {VAULT_V2_INTEREST_PAUSED_TOOLTIP}</span></div>;
    $[32] = vaultV2.maxApy;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== address || $[35] !== aggregatedWarnings || $[36] !== formattedTitle || $[37] !== titleColor || $[38] !== vaultV2.name) {
    t11 = <div className="gap-lg flex items-center">{vaultV2.name ? <div className="max-md:gap-xs flex flex-wrap gap-[24px]"><h1 className="max-md:gap-xs flex flex-wrap gap-[24px]"><ResponsiveVaultTitle color={titleColor}>{formattedTitle.first}</ResponsiveVaultTitle><ResponsiveVaultTitle color={titleColor || "text.tertiary"}>{formattedTitle.rest}</ResponsiveVaultTitle></h1><WarningIcon warnings={aggregatedWarnings} iconSize={50} /></div> : <div className="gap-sm flex flex-wrap items-center"><h1 className="flex flex-wrap items-center"><ResponsiveVaultTitle color={titleColor}>{abbreviateAddress(address)}</ResponsiveVaultTitle></h1><WarningIcon warnings={aggregatedWarnings} iconSize={50} /></div>}</div>;
    $[34] = address;
    $[35] = aggregatedWarnings;
    $[36] = formattedTitle;
    $[37] = titleColor;
    $[38] = vaultV2.name;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== pendingActionsSlot) {
    t12 = <div className="max-md:hidden">{pendingActionsSlot}</div>;
    $[40] = pendingActionsSlot;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== t11 || $[43] !== t12) {
    t13 = <div className="gap-lg flex w-[100%] flex-row items-center justify-between">{t11}{t12}</div>;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== address || $[46] !== chainId) {
    t14 = ChainUtils.getExplorerAddressUrl(chainId, address);
    $[45] = address;
    $[46] = chainId;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== address) {
    t15 = abbreviateAddress(address);
    $[48] = address;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== t15) {
    t16 = {
      variant: "ghost",
      label: t15,
      size: "default"
    };
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== t14 || $[53] !== t16) {
    t17 = <TooltipTrigger asChild={true}><span><ButtonLink href={t14} mode="external" buttonProps={t16} /></span></TooltipTrigger>;
    $[52] = t14;
    $[53] = t16;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <TooltipContent>View on chain explorer</TooltipContent>;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== t17) {
    t19 = <Tooltip>{t17}{t18}</Tooltip>;
    $[56] = t17;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== chainId) {
    t20 = <NetworkAvatar chainId={chainId} />;
    $[58] = chainId;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== chain.chain.name) {
    t21 = <span className="text-2xs text-foreground">{chain.chain.name}</span>;
    $[60] = chain.chain.name;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== t20 || $[63] !== t21) {
    t22 = <div className="gap-2xs flex items-center">{t20}{t21}</div>;
    $[62] = t20;
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      flexWrap: "wrap"
    };
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== vaultV2.curators) {
    t24 = vaultV2.curators.map(_temp3);
    $[66] = vaultV2.curators;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== t24) {
    t25 = <div className="gap-xs flex items-center" style={t23}>{t24}</div>;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== asset) {
    t26 = asset && <TokenWithHoverCard asset={asset} />;
    $[70] = asset;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== pendingActionsSlot) {
    t27 = <div className="md:hidden">{pendingActionsSlot}</div>;
    $[72] = pendingActionsSlot;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== t19 || $[75] !== t22 || $[76] !== t25 || $[77] !== t26 || $[78] !== t27) {
    t28 = <div className="gap-sm flex flex-wrap items-center">{t19}{t22}{t25}{t26}{t27}</div>;
    $[74] = t19;
    $[75] = t22;
    $[76] = t25;
    $[77] = t26;
    $[78] = t27;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== t13 || $[81] !== t28) {
    t29 = <div className="gap-sm flex w-[100%] flex-col">{t13}{t28}</div>;
    $[80] = t13;
    $[81] = t28;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  let t30;
  if ($[83] !== vaultV2.metadata) {
    t30 = vaultV2.metadata?.description && <LinkifiedVaultDescription description={vaultV2.metadata.description} />;
    $[83] = vaultV2.metadata;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  let t31;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="flex items-center"><span className="text-secondary-foreground text-xs" style={{
        whiteSpace: "nowrap"
      }}>Total Deposits</span></div>;
    $[85] = t31;
  } else {
    t31 = $[85];
  }
  const t32 = totalDepositsUsd ? `$${totalDepositsUsd.value}` : "-";
  let t33;
  if ($[86] !== t32) {
    t33 = <span className="text-title-sm">{t32}</span>;
    $[86] = t32;
    $[87] = t33;
  } else {
    t33 = $[87];
  }
  const t34 = totalDepositsUsd?.unit;
  let t35;
  if ($[88] !== t34) {
    t35 = <span className="text-title-sm text-muted-foreground">{t34}</span>;
    $[88] = t34;
    $[89] = t35;
  } else {
    t35 = $[89];
  }
  let t36;
  if ($[90] !== t33 || $[91] !== t35) {
    t36 = <div className="flex items-center">{t33}{t35}</div>;
    $[90] = t33;
    $[91] = t35;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] !== totalDeposits) {
    t37 = <span className="text-xs">{totalDeposits}</span>;
    $[93] = totalDeposits;
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  let t38;
  if ($[95] !== t36 || $[96] !== t37) {
    t38 = <div className="gap-sm flex flex-1 flex-col">{t31}{t36}{t37}</div>;
    $[95] = t36;
    $[96] = t37;
    $[97] = t38;
  } else {
    t38 = $[97];
  }
  let t39;
  if ($[98] !== asset || $[99] !== vaultV2) {
    t39 = <VaultV2LiquidityHoverCard vaultV2={vaultV2} asset={asset} />;
    $[98] = asset;
    $[99] = vaultV2;
    $[100] = t39;
  } else {
    t39 = $[100];
  }
  let t40;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="flex items-center gap-[6px]"><span className="text-secondary-foreground text-xs" style={{
        whiteSpace: "nowrap"
      }}>Exposure</span></div>;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  let t41;
  if ($[102] !== collateralExposures || $[103] !== totalAssetsUsd) {
    t41 = <div className="gap-sm flex flex-1 flex-col">{t40}{collateralExposures ? <CollateralExposureStack collateralExposures={collateralExposures} totalAssetsUsd={totalAssetsUsd} maxTokensCount={5} size="regular" /> : <span className="text-title-sm" data-testid="vault-v2-hero-exposure-missing">-</span>}</div>;
    $[102] = collateralExposures;
    $[103] = totalAssetsUsd;
    $[104] = t41;
  } else {
    t41 = $[104];
  }
  let t42;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = {
      whiteSpace: "nowrap"
    };
    $[105] = t42;
  } else {
    t42 = $[105];
  }
  let t43;
  if ($[106] !== netApyLabelId) {
    t43 = <span id={netApyLabelId} className="text-secondary-foreground text-xs" style={t42}>Net APY</span>;
    $[106] = netApyLabelId;
    $[107] = t43;
  } else {
    t43 = $[107];
  }
  let t44;
  if ($[108] !== apy || $[109] !== asset || $[110] !== hasNetApyCard || $[111] !== includesDonations || $[112] !== isRateCapped || $[113] !== managementFee || $[114] !== maxRate || $[115] !== netApy || $[116] !== performanceFee || $[117] !== points || $[118] !== rewards || $[119] !== vaultV2.apyBreakdown || $[120] !== vaultV2.maxApy) {
    t44 = hasNetApyCard && <InfoHoverCardTrigger onOpenChange={_temp4}><RewardsHoverCard performanceFee={performanceFee} managementFee={managementFee} apy={apy} variant="vault" netApy={netApy} asset={asset} rewards={rewards} points={points} maxRate={maxRate} maxApy={vaultV2.maxApy} isRateCapped={isRateCapped} includesDonations={includesDonations} apyBreakdown={vaultV2.apyBreakdown} /></InfoHoverCardTrigger>;
    $[108] = apy;
    $[109] = asset;
    $[110] = hasNetApyCard;
    $[111] = includesDonations;
    $[112] = isRateCapped;
    $[113] = managementFee;
    $[114] = maxRate;
    $[115] = netApy;
    $[116] = performanceFee;
    $[117] = points;
    $[118] = rewards;
    $[119] = vaultV2.apyBreakdown;
    $[120] = vaultV2.maxApy;
    $[121] = t44;
  } else {
    t44 = $[121];
  }
  let t45;
  if ($[122] !== t43 || $[123] !== t44) {
    t45 = <div className="gap-2xs flex items-center">{t43}{t44}</div>;
    $[122] = t43;
    $[123] = t44;
    $[124] = t45;
  } else {
    t45 = $[124];
  }
  let t46;
  if ($[125] !== apy || $[126] !== asset || $[127] !== includesDonations || $[128] !== isRateCapped || $[129] !== managementFee || $[130] !== maxRate || $[131] !== netApy || $[132] !== performanceFee || $[133] !== points || $[134] !== rewards || $[135] !== vaultV2.apyBreakdown || $[136] !== vaultV2.maxApy) {
    t46 = {
      performanceFee,
      managementFee,
      apy,
      variant: "vault",
      netApy,
      asset,
      rewards,
      points,
      maxRate,
      maxApy: vaultV2.maxApy,
      isRateCapped,
      includesDonations,
      apyBreakdown: vaultV2.apyBreakdown
    };
    $[125] = apy;
    $[126] = asset;
    $[127] = includesDonations;
    $[128] = isRateCapped;
    $[129] = managementFee;
    $[130] = maxRate;
    $[131] = netApy;
    $[132] = performanceFee;
    $[133] = points;
    $[134] = rewards;
    $[135] = vaultV2.apyBreakdown;
    $[136] = vaultV2.maxApy;
    $[137] = t46;
  } else {
    t46 = $[137];
  }
  let t47;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = {
      whiteSpace: "nowrap"
    };
    $[138] = t47;
  } else {
    t47 = $[138];
  }
  let t48;
  if ($[139] !== formattedNetApy) {
    t48 = <span className="text-title-sm">{formattedNetApy}</span>;
    $[139] = formattedNetApy;
    $[140] = t48;
  } else {
    t48 = $[140];
  }
  let t49;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <span className="gap-2xs flex items-center"><span className="text-title-sm text-muted-foreground">%</span></span>;
    $[141] = t49;
  } else {
    t49 = $[141];
  }
  let t50;
  if ($[142] !== t48) {
    t50 = <span data-testid="vault-v2-hero-net-apy" className="flex" style={t47}>{t48}{t49}</span>;
    $[142] = t48;
    $[143] = t50;
  } else {
    t50 = $[143];
  }
  let t51;
  if ($[144] !== netApyLabelId || $[145] !== t46 || $[146] !== t50) {
    t51 = <IconRewardHover labelledBy={netApyLabelId} RewardsHoverCardProps={t46} iconSize={35}>{t50}</IconRewardHover>;
    $[144] = netApyLabelId;
    $[145] = t46;
    $[146] = t50;
    $[147] = t51;
  } else {
    t51 = $[147];
  }
  let t52;
  if ($[148] !== t45 || $[149] !== t51) {
    t52 = <div className="gap-sm flex flex-1 flex-col">{t45}{t51}</div>;
    $[148] = t45;
    $[149] = t51;
    $[150] = t52;
  } else {
    t52 = $[150];
  }
  let t53;
  if ($[151] !== t38 || $[152] !== t39 || $[153] !== t41 || $[154] !== t52) {
    t53 = <div className="gap-lg flex w-full flex-col justify-start md:flex-row md:flex-wrap">{t38}{t39}{t41}{t52}</div>;
    $[151] = t38;
    $[152] = t39;
    $[153] = t41;
    $[154] = t52;
    $[155] = t53;
  } else {
    t53 = $[155];
  }
  let t54;
  if ($[156] !== t10 || $[157] !== t29 || $[158] !== t30 || $[159] !== t53) {
    t54 = <div data-testid="vault-hero" className="flex w-full flex-col items-start gap-[48px]">{t10}{t29}{t30}{t53}</div>;
    $[156] = t10;
    $[157] = t29;
    $[158] = t30;
    $[159] = t53;
    $[160] = t54;
  } else {
    t54 = $[160];
  }
  return t54;
};
export default VaultV2HeroClient;
function _temp(reward) {
  return Number(reward.apr ?? ZERO) / 1000000000000000000;
}
function _temp2(exposure) {
  return {
    token: parseApiAsset(exposure.token),
    supplyAssetsUsd: safeParseNumber(exposure.supplyAssetsUsd),
    isIdle: exposure.isIdle
  };
}
function _temp3(curator) {
  return <div className="gap-xs max-md:gap-2xs flex items-center" key={curator.id}><ButtonLink href={getCuratorUrl({
      curatorId: curator.id
    })} mode="internal" buttonProps={{
      variant: "ghost",
      label: curator.name,
      size: "default",
      avatarProps: {
        src: curator.image
      }
    }} /></div>;
}
function _temp4(isOpen) {
  return trackMetricTooltipOpenChange(isOpen, {
    page: "vault",
    metric: "net_apy",
    vault_version: "v2"
  });
}